import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Stack,
    Pagination,
    TextField,
    Skeleton,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ClearBtn, DeleteBtn, SearchBtn } from '../../Buttons/Buttons';
import * as HeaderAuthentication from '../../../components/HeaderAuthentication'
import { deleteTrashedData, postDeleteViewedContactsData, postPhoneUnlockDatas, restoreTrashedData, unlockAllViewedContacts } from '../../redux/action';
import { useDispatch } from 'react-redux';
import { Circles } from 'react-loader-spinner';
import noDataGif from '../../../assets/images/No data.gif'
import { Modal } from 'react-bootstrap';
dayjs.extend(relativeTime);

export default function TrashTable({
    page,
    setPage,
    rowsPerPage,
    totalDataCount,
    loading,
    fetchTashedData,
    loadingMore,
    setLoadingMore,
    tableHeight,
    selectedRowsData,
    setSelectedRowsData,
    trashedData,
    div2Ref,
    handleDownload,
    signInID,
    sbnID,
    role
}) {

    const dispatch = useDispatch();
    const signInDetails = JSON.parse(sessionStorage.getItem("SignInDetails"));

    const [pageInput, setPageInput] = React.useState('');
    const [selectedRowIds, setSelectedRowIds] = React.useState([]); // array of selected row IDs
    const [resetKey, setResetKey] = React.useState(0);
    const [selectedRowIdForAction, setSelectedRowIdForAction] = React.useState(null);

    // Modal state
    const [showConfirmModal, setShowConfirmModal] = React.useState(false);
    const [confirmAction, setConfirmAction] = React.useState(null);

    const renderSkeletonCell = (params) =>
        params.row?.isSkeleton ? (
            <Skeleton
                variant="text"
                width={`${Math.floor(Math.random() * (80 - 40 + 1)) + 40}%`}
            />
        ) : (
            params.value || 'NA'
        );

    // Define DataGrid columns
    const columns = [
        { field: 'id', headerName: 'ID', width: 70, renderCell: renderSkeletonCell },
        { field: 'firstName', headerName: 'First Name', width: 130, renderCell: renderSkeletonCell },
        { field: 'lastName', headerName: 'Last Name', width: 130, renderCell: renderSkeletonCell },
        { field: 'jobLevel', headerName: 'Job Level', width: 180, renderCell: renderSkeletonCell },
        { field: 'jobDepartment', headerName: 'Job Department', width: 180, renderCell: renderSkeletonCell },
        { field: 'title', headerName: 'Title', width: 220, renderCell: renderSkeletonCell },
        { field: 'company', headerName: 'Company', width: 200, renderCell: renderSkeletonCell },
        { field: 'website', headerName: 'Website', width: 180, renderCell: renderSkeletonCell },
        { field: 'phone', headerName: 'phone', width: 130, renderCell: renderSkeletonCell },
        { field: 'email', headerName: 'Email', width: 200, renderCell: renderSkeletonCell },
        { field: 'industry', headerName: 'Industry', width: 180, renderCell: renderSkeletonCell },
        { field: 'subIndustry', headerName: 'SubIndustry', width: 250, renderCell: renderSkeletonCell },
        { field: 'technology', headerName: 'Technology', width: 150, renderCell: renderSkeletonCell },
        { field: 'city', headerName: 'City', width: 130, renderCell: renderSkeletonCell },
        { field: 'state', headerName: 'State', width: 130, renderCell: renderSkeletonCell },
        { field: 'country', headerName: 'Country', width: 130, renderCell: renderSkeletonCell },
        { field: 'zipCode', headerName: 'Zip Code', width: 130, renderCell: renderSkeletonCell },
        { field: 'sicCode', headerName: 'Sic Code', width: 130, renderCell: renderSkeletonCell },
        { field: 'revenue', headerName: 'Revenue', width: 150, renderCell: renderSkeletonCell },
        { field: 'employee', headerName: 'Employee', width: 150, renderCell: renderSkeletonCell },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                return (
                    <Box className='trash-table-action-div'>
                        <i
                            className="fa-solid fa-trash-arrow-up restore"
                            title="Restore"
                            onClick={() => {
                                setConfirmAction("restore");
                                setSelectedRowIdForAction(params.row.id);
                                setShowConfirmModal(true);
                            }}
                        ></i>
                        <i
                            className="fa-solid fa-trash delete"
                            title="Delete"
                            onClick={() => {
                                setConfirmAction("delete");
                                setSelectedRowIdForAction(params.row.id);
                                setShowConfirmModal(true);
                            }}
                        ></i>
                    </Box>
                );
            },
        }
    ];

    // Helper to map API response to DataGrid row format
    const transformSearchContactData = (data) => {
        return data.map((item, index) => ({
            id: (index + 1),
            apiId: item.id,
            firstName: item.firstname || 'NA',
            lastName: item.lastname || 'NA',
            jobLevel: item.job_level || 'NA',
            jobDepartment: item.job_department || 'NA',
            title: item.job_title || 'NA',
            company: item.company || 'NA',
            website: item.website || 'NA',
            phone: item.phone || 'NA',
            email: item.email || 'NA',
            industry: item.industry || 'NA',
            subIndustry: item.sub_industry || 'NA',
            technology: item.technology || 'NA',
            city: item.city || 'NA',
            state: item.state || 'NA',
            country: item.country || 'NA',
            zipCode: item.zipcode || 'NA',
            sicCode: item.sic_code || 'NA',
            revenue: item.revenue || 'NA',
            employee: item.employee_size || 'NA',
            list_name: item.list_name,
        }));
    };

    const rows = loading
        ? Array.from({ length: 10 }).map((_, index) => ({
            id: index + 1,
            apiId: null,
            firstName: '',
            lastName: '',
            jobLevel: '',
            jobDepartment: '',
            title: '',
            company: '',
            website: '',
            phone: '',
            email: '',
            industry: '',
            subIndustry: '',
            technology: '',
            city: '',
            state: '',
            country: '',
            zipCode: '',
            sicCode: '',
            revenue: '',
            employee: '',
            list_name: '',
            isSkeleton: true,
        }))
        : Array.isArray(trashedData) && trashedData?.length > 0
            ? transformSearchContactData(trashedData)
            : [];

    const totalPages = Math.ceil(totalDataCount / rowsPerPage);

    const paginatedRows = rows;

    const handlePageChange = (event, value) => {
        setPage(value);
        setPageInput('');
    };

    const handleGoToPage = () => {
        const pageNumber = parseInt(pageInput, 10);
        if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
            setPage(pageNumber);
        }
    };

    const scrollContainerRef = React.useRef(null);

    React.useEffect(() => {
        const container = document.querySelector('.MuiDataGrid-virtualScroller');
        if (!container) return;

        let lastScrollTop = 0;

        const handleScroll = () => {
            if (!container || loadingMore) return;

            const currentScrollTop = container.scrollTop;

            if (currentScrollTop <= lastScrollTop) return;

            lastScrollTop = currentScrollTop;

            const distanceFromBottom = container.scrollHeight - currentScrollTop - container.clientHeight;

            if (distanceFromBottom < 10 && trashedData?.length < totalDataCount && !loadingMore) {
                setLoadingMore(true);
                fetchTashedData();
                setPage((prev) => prev + 1);
            }
        };

        container.addEventListener('scroll', handleScroll);

        // Cleanup function
        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [loadingMore, trashedData?.length, totalDataCount, setPage]);
    
    const handleTrashedRestoredContactsAction = (actionType, rowId = null) => {
        let type = "all";
        let contacts = [];

        if (selectedRowIds.length > 0) {
            // Multi-select
            type = "selected";
            contacts = selectedRowIds
                .map((rowId) => {
                    const row = paginatedRows.find((r) => r.id === rowId);
                    return row?.apiId
                        ? {
                            contact_id: parseInt(row.apiId),
                            list_name: row.list_name || "Default",
                        }
                        : null;
                })
                .filter(Boolean);
        } else if (rowId) {
            // Row-level icon click
            type = "selected";
            const row = paginatedRows.find((r) => r.id === rowId);
            if (row?.apiId) {
                contacts.push({
                    contact_id: parseInt(row.apiId),
                    list_name: row.list_name || "Default",
                });
            }
        }

        const requestPayload = {
            user_id: parseInt(signInDetails[0]?.id),
            user_name: signInDetails[0]?.name,
            sbn_id: signInDetails[0]?.sbn_id,
            role: signInDetails[0]?.role,
            type,
            contacts,
        };

        const requestOption = {
            method: "POST",
            mode: "cors",
            headers: HeaderAuthentication.postAuthOfLeadMerge,
            body: JSON.stringify(requestPayload),
        };

        const clearSelection = () => {
            setSelectedRowIds([]);        
            setSelectedRowsData([]);      
            setResetKey(prev => prev + 1);  
        };

        if (actionType === "restore") {
            dispatch(restoreTrashedData(requestOption, fetchTashedData, clearSelection));
        } else if (actionType === "delete") {
            dispatch(deleteTrashedData(requestOption, fetchTashedData, clearSelection));
        }
    };

    const CustomNoRowsOverlay = () => (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            p={2}
        >
            <img src={noDataGif} alt="No Data" width={180} style={{ marginBottom: 10 }} />
            <Typography variant="subtitle1" color="textSecondary">
                No Data Available
            </Typography>
        </Box>
    );

    const handleConfirmAction = () => {
        if (confirmAction === 'restore') {
            handleTrashedRestoredContactsAction("restore", selectedRowIdForAction);
        } else if (confirmAction === 'delete') {
            handleTrashedRestoredContactsAction("delete", selectedRowIdForAction);
        }

        setShowConfirmModal(false);
        setConfirmAction(null);
        setSelectedRowIdForAction(null);
    };

    return (
        <>
            <div className='listname-count-div' ref={div2Ref}>
                <p>List Name- Contacts &nbsp;(<span>{totalDataCount}</span>)</p>
                
                <div className='view-contacts-btn'>
                    {totalDataCount !== 0 && (
                        <DeleteBtn
                            btnName="Empty Trash"
                            onClickEffect={() => {
                                setConfirmAction('delete');
                                setShowConfirmModal(true);
                            }}
                        />
                    )}
                    {totalDataCount !== 0 && (
                        <SearchBtn
                            btnName={selectedRowIds.length > 0 ? "Restore" : "Restore All"}
                            onClickEffect={() => {
                                setConfirmAction('restore');
                                setShowConfirmModal(true);
                            }}
                        />
                    )}
                </div>
            </div>
            <div style={{ position: 'relative' }}>
                <Box sx={{ width: '100%', mt: 2 }}>
                    <DataGrid
                        key={resetKey}
                        sx={{ height: `${tableHeight}px !important` }}
                        ref={scrollContainerRef}
                        pagination={false}
                        rowCount={totalDataCount}
                        paginationMode="server"
                        rows={paginatedRows}
                        columns={columns}
                        hideFooter
                        checkboxSelection
                        className="search-contact-data-grid"
                        getRowId={(row) => row.id}
                        onRowClick={(row) => row.id}
                        getRowClassName={(params) =>
                            params.row?.isSkeleton ? 'skeleton-row' : ''
                        }
                        onRowSelectionModelChange={(newSelection) => {
                            const selectedIds = Array.from(newSelection?.ids ?? []);
                            setSelectedRowIds(selectedIds);
                            const selectedData = paginatedRows.filter((row) => selectedIds.includes(row.id));
                            setSelectedRowsData(selectedData);
                        }}
                        selectionModel={selectedRowIds}
                        loading={loading}
                        components={{
                            NoRowsOverlay: CustomNoRowsOverlay, 
                        }}
                    />
                    {loadingMore && <div className='loader-css'>
                        <div className="d-flex justify-content-center align-items-center py-4">
                            <Circles
                                height="30"
                                width="30"
                                color="#007bff"
                                ariaLabel="circles-loading"
                                visible={true}
                            />
                        </div>
                    </div>}
                    
                </Box>
            </div>

            {/* Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered className="saved-search-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Action</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to&nbsp;
                    {confirmAction === 'restore'
                        ? selectedRowIdForAction
                            ? 'restore this contact?'
                            : 'restore all contacts?'
                        : selectedRowIdForAction
                            ? 'permanently delete this contact?'
                            : 'permanently delete all contacts?'}
                </Modal.Body>
                <Modal.Footer>
                    <ClearBtn btnName="Cancel" onClickEffect={() => setShowConfirmModal(false)} />
                    <SearchBtn btnName="Ok" onClickEffect={handleConfirmAction} />
                </Modal.Footer>
            </Modal>
        </>
    );
}

